import React from "react";

import { Pressable } from "../../../pressable";
import { ContentCard } from "../../../content-card";
import { ContentCardCaption } from "../../../content-card/caption";
import { ProviderIcon } from "../../../video/provider-icon";
import { Video } from "../../../video";
import { useElementAppearsOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSnowplowContext } from "../../../../analytics/snowplow";
import { OPEN_LINK } from "../../../../constants/types/commands";
import { useSendCommand } from "../../../../hooks/use-send-command";

import styles from "./styles.modules.scss";

export type VideoArticleType = {
    type: "communityEntry" | "liveStreamCommunityEntry" | "videoNewsEntry";
    categories?: { title: string; color: string }[];
    creator?: {
        name: string;
        icon: string;
    };
    embed: {
        id: string;
        livepid?: string;
        provider: string;
        url: string;
    };
    embedVideo: boolean;
    id: string;
    index: number;
    lang: string;
    listingSize: string;
    live: boolean;
    newUntil: number;
    postDate: number;
    section: "newsEntries" | "esportNewsEntries" | "communityEntries";
    thumbnail: ImageSourceSets;
    thumbnailAlt: ImageSourceSets;
    title: string;
};

export const VideoArticle = ({
    categories,
    embed,
    embedVideo,
    id,
    lang,
    live,
    newUntil,
    postDate,
    thumbnail,
    thumbnailAlt,
    type,
    title,
    creator,
    listingSize,
    index,
}: VideoArticleType) => {
    const sendCommand = useSendCommand();

    const { name: creatorName, icon } = creator || {};
    const { url } = embed;

    const onClick = () =>
        !embedVideo &&
        sendCommand(OPEN_LINK, {
            url,
            id,
            label: title,
            additionalInformation: { inboxFeedIndex: index },
        });

    const small = !embedVideo && listingSize === "small";

    const [ref, isVisible] = useElementAppearsOnScreen<HTMLDivElement>();
    const { trackImpression } = useSnowplowContext();
    React.useEffect(() => {
        if (isVisible) {
            trackImpression(id, title);
        }
    }, [id, isVisible]);

    return (
        <div
            className={`article-video ${styles.articleVideo} ${
                small ? `small ${styles.small}` : ""
            } `}
            data-testid={type}
            onClick={onClick}
            ref={ref}
        >
            <Pressable animationSize="large">
                <div className={`card ${styles.card}`}>
                    <ContentCard id={id} lang={lang} newUntil={newUntil}>
                        <div className={`content ${styles.content}`}>
                            <div className={`video ${styles.video}`}>
                                <Video
                                    containerId={`video-news-${id}`}
                                    embedVideo={embedVideo}
                                    embed={embed}
                                    hideFilter={small}
                                    hideProvider={small}
                                    live={live}
                                    thumbnail={small ? thumbnailAlt : thumbnail}
                                />
                            </div>
                            <div className={`caption ${styles.caption}`}>
                                <ContentCardCaption
                                    creator={creatorName}
                                    cardId={id}
                                    categories={categories}
                                    ctaHidden={small || embedVideo}
                                    external={!embedVideo}
                                    icon={icon}
                                    postDate={postDate}
                                    title={title}
                                    provider={embed.provider}
                                    type={type}
                                />
                            </div>
                            {small && (
                                <div className={`provider ${styles.provider} `}>
                                    <ProviderIcon
                                        provider={embed.provider}
                                        variant="small"
                                    />
                                </div>
                            )}
                        </div>
                    </ContentCard>
                </div>
            </Pressable>
        </div>
    );
};
